<template>
  <div class="form-field">
    <div
      v-if="
        $slots.hint &&
        schema.hintPosition === 'top' &&
        field.type !== 'checkbox' &&
        field.type !== 'radio'
      "
      class="hint"
    >
      <slot name="hint"></slot>
    </div>
    <div v-if="options && options.length > 0">
      <label v-if="!field.hideLabel">
        <slot name="label">
          {{ field.label ? field.label : "&nbsp;" }}
          <span class="required" v-if="field.label && field.required">*</span>
        </slot>
        <div v-if="hasHelpSlot" class="help">
          <icon icon="info" />
          <div class="tooltip" :class="field.tooltipLocation">
            <slot name="help"> Shalom from the tooltip! </slot>
          </div>
        </div>
      </label>
      <div v-if="$slots.hint && schema.hintPosition === 'top'" class="hint">
        <slot name="hint"></slot>
      </div>
      <div v-bind="$attrs" class="radio-btns-container">
        <label v-for="option in options" :key="option.value" class="radio-container">
          <span class="label">{{ option.label ? option.label : "" }}</span>
          <input
            type="radio"
            ref="radioInput"
            :value="option.value"
            :name="field.label || 'radio-group'"
            :checked="(modelValue as any)"
            @change="
              $emit('update:modelValue', option.value !== undefined ? option.value : option);
              validate(schema, ($event.target as any).checked, compare);
            "
            v-bind="$attrs"
          />
          <span class="checkmark"></span>
          <slot></slot>
        </label>
      </div>
    </div>
    <div v-if="$slots.hint && schema.hintPosition === 'bottom'" class="hint">
      <slot name="hint"></slot>
    </div>
    <ul
      class="invalid-feedback"
      :class="{
        multiple: schema.errors && schema.errors.length > 1,
      }"
    >
      <li v-for="(error, index) in schema.errors" :key="index">
        {{ error }}
      </li>
    </ul>
  </div>
</template>
<script lang="ts">
import { FieldSchema, FormField, Option } from "@/models/form";
import useValidation from "@/use/validation";
import { defineComponent, PropType } from "vue";
import Icon from "@/components/shared/Icon.vue";

export default defineComponent({
  components: { Icon },
  setup() {
    const { validate, validateForm } = useValidation();
    return {
      validate,
      validateForm,
    };
  },
  props: {
    schema: {
      type: Object as PropType<FieldSchema>,
      required: true,
    },
    modelValue: {
      type: [String, Number, Boolean, Object],
      default: "",
    },
    options: {
      type: Array as PropType<Array<Option>>,
      default: [] as Array<Option>,
    },
    compare: {
      type: [String, Number, Boolean, Object],
      required: false,
    },
  },
  emits: ["update:modelValue"],
  data: () => ({
    field: {} as FormField,
  }),
  computed: {
    hasHelpSlot(): boolean {
      return !!this.$slots.help;
    },
  },
  methods: {},
  beforeMount() {
    // Take in schema and create the form field
    if (this.schema) {
      this.field = {
        ...this.schema,
        touched: false,
        valid: false,
      } as FormField;
    }

    // If we don't do this it defaults booleans as true
    if (this.modelValue === "" || this.modelValue === undefined) {
      this.$emit("update:modelValue", false);
    }
  },
});
</script>
<style lang="scss" scoped>
.form-field {
  display: flex;
  flex-direction: column;
  font-family: var(--body-font);
  font-weight: 400;
  font-size: 1rem;
  color: #333333;

  .hint {
    font-size: 0.875rem; // 14px
    font-weight: 400;
    margin-bottom: 0.5em;
  }

  label {
    font-family: var(--body-font);
    font-weight: 400;
    font-size: 1.25rem;
    color: #333333;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;

    .required {
      color: #8b0e04;
    }

    .help {
      position: relative;
      display: inline-block;
      background-size: 18px 18px;
      height: 16px;
      width: 16px;
      margin-left: 5px;
      color: #767676;
      /* Tooltip container */
      .tooltip {
        visibility: hidden;
        height: fit-content;
        width: fit-content;
        overflow: hidden;
        max-width: 90vw;
        background-color: #fff;
        border-radius: 6px;
        padding: 15px;
        font-size: 0.9rem;
        font-weight: 400;
        color: #767676;
        box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
        -webkit-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
        -moz-box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);

        /* Position the tooltip */
        position: absolute;
        z-index: 1;

        &:before {
          content: " ";
          display: block;
          position: absolute;
          left: 54px;
          bottom: -6px;
          width: 14px;
          height: 14px;
          border-width: 0;
          border-style: none solid solid none;
          background-color: #fff;
          box-shadow: 20px 3px 15px rgba(19, 17, 17, 0.2);
          -webkit-box-shadow: 20px 3px 15px rgba(0, 0, 0, 0.2);
          -moz-box-shadow: 20px 3px 15px rgba(0, 0, 0, 0.2);
          transform: rotate(45deg);
          -webkit-transform: rotate(45deg);
          -moz-transform: rotate(45deg);
        }

        &.bottom {
          top: 23px;

          &:before {
            top: -7px;
            box-shadow: -13px -5px 15px rgba(19, 17, 17, 0.2);
            -webkit-box-shadow: -13px -5px 15px rgba(19, 17, 17, 0.2);
            -moz-box-shadow: -13px -5px 15px rgba(19, 17, 17, 0.2);
          }
        }
      }

      &:hover,
      &:focus {
        .tooltip {
          visibility: visible;
          min-width: 120px;
          bottom: 175%;
          left: 50%;
          margin-left: -60px; /* Use half of the width (120/2 = 60), to center the tooltip */
        }
      }
    }
  }

  .radio-btns-container {
    display: flex;

    &.inline {
      display: block;
    }

    /* The container */
    .radio-container {
      position: relative;
      padding-left: 32px;
      margin-bottom: 0px;
      margin-right: 30px;
      cursor: pointer;
      font-size: 20px;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      /* Hide the browser's default radio button */
      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }

      .label {
        vertical-align: middle;
        font-weight: 400;
        font-family: var(--body-font);
        font-size: 1.25rem;
        color: #333333;
      }

      /* On mouse-over, add a grey background color */
      &:hover input ~ .checkmark {
        background-color: #fff;
      }

      /* Create a custom radio button */
      .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 20px;
        width: 20px;
        background-color: #fff;
        border: 2px solid lightgray;
        border-radius: 50%;

        /* Create the indicator (the dot/circle - hidden when not checked) */
        &:after {
          content: "";
          position: absolute;
          display: none;
        }
      }

      /* Show the indicator (dot/circle) when checked */
      input:checked ~ .checkmark:after {
        display: block;
      }

      /* Style the indicator (dot/circle) */
      .checkmark:after {
        top: 3px;
        left: 3px;
        height: 10px;
        width: 10px;
        border-radius: 50%;
        background: var(--theme-color);
      }
    }
  }

  .radio-btns-container.active {
    .label {
      font-weight: 600;
    }
  }
  .invalid-feedback {
    list-style: none;
    color: #8b0e04;
    padding-left: 0;
    margin: 0 0 0.5em;
  }
}

// Small and medium devices (landscape phones,tablets 992px and down)
@media (max-width: 992px) {
  .form-field {
    .radio-btns-container {
      /* The container */
      .radio-container {
        position: relative;
        line-height: 1.5;
        font-size: 18px;
        /* Create a custom radio button */
        .checkmark {
          position: absolute;
          width: 1.25em;
          height: 1.25em;
        }
        /* Style the indicator (dot/circle) */
        .checkmark:after {
          width: 0.75em;
          height: 0.75em;
        }
      }
    }
    label {
      font-size: 0.9em;
      line-height: 18px;

      .help {
        position: inherit;

        &:hover, &:focus {
          .tooltip {
            right: 0;
            left: 0;
            top: 0;
            bottom: 0;
            max-width: calc(100% - 30px);
            margin-left: 0;
          }
        }
      }
    }

    .invalid-feedback {
      font-size: 0.8rem;
    }
  }
}
</style>
